import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 4.5 visual preview at /preview/lesson/4.5
 * "Week 4 capstone: Care about who reads it"
 * 8-section format like 3.5.
 */

export default function LessonPreviewPage4_5() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-brief',
  'sec-five-moves',
  'sec-stage-1',
  'sec-stage-2',
  'sec-stage-3',
  'sec-stage-4',
  'sec-forward',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 8;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[210px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <Brief />
          <FiveMoves />
          <Stage1 />
          <Stage2 />
          <Stage3 />
          <Stage4 />
          <CarryForward />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 4 · Doing the work AI cannot do · Lesson 4.5 · Capstone
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Care about who reads it
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 55 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Judgment · Voice · Character · Reader</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        No new vocab. One reader. Every move from 4.1 to 4.4.
      </p>
    </header>
  );
}

/* --- Section 1: Brief --- */
function Brief() {
  return (
    <Section id="sec-brief" kicker="The brief" title="Pick a real reader. Write 500 words they would actually want to read.">
      <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Assignment
        </p>
        <h3 className="mt-2 text-xl font-semibold text-text leading-snug">
          Pick one real person who is not your current teacher. Write 500 words
          that person would actually want to read.
        </h3>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <BriefRow label="Length" value="500 words. Roughly one page, single spaced." />
          <BriefRow label="Topic" value="Your choice. Pick something the reader and you both have access to." />
          <BriefRow label="Reader" value="Pick one. Real human. You know them by name." />
          <BriefRow label="Goal" value="They keep reading to the last sentence." />
        </div>
      </div>

      <p className="mt-6 text-text/90">
        This is the capstone. Every move from this week runs on one real piece
        of writing, addressed to one real reader. The reader is not your English
        teacher. The reader is somebody you actually know, who you actually want
        to land this with.
      </p>

      <Subhead n={1} title="Three reader options" className="mt-10" />
      <p className="mt-4 text-text/90">
        Pick one. The choice changes everything about the writing.
      </p>
      <ReaderOptions />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Before you read on
        </p>
        <p className="mt-3 text-text/90">
          Pick a reader right now. Write their name on a piece of paper. Write
          one sentence about what they already know that most people do not.
          That sentence is the start of your through-line.
        </p>
      </div>
    </Section>
  );
}

function BriefRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-line bg-surface p-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/90">{value}</p>
    </div>
  );
}

const READERS = [
  {
    n: '01',
    name: 'A younger sibling',
    accent: 'emerald' as const,
    knows: 'You. Your family. The same kitchen. Your inside jokes.',
    does_not: 'Most of what you have figured out in the last two years.',
    write_about: 'Something you know now that you wish you had known then. A specific moment from your year that changed how you see something. Why you finally stopped being scared of the thing they are currently scared of.',
  },
  {
    n: '02',
    name: 'A teacher who taught you last year',
    accent: 'amber' as const,
    knows: 'Your writing voice. What you used to be bad at. The fight you had with one essay.',
    does_not: 'How the year is going, what you are reading on your own, what you actually think now.',
    write_about: 'Something you finally understood after their class ended. Something you would tell them about a current assignment if you could sit at their desk. A book you have read since their class that they would want to know about.',
  },
  {
    n: '03',
    name: 'A coach',
    accent: 'emerald' as const,
    knows: 'Every game. Every drill. The team. The season.',
    does_not: 'What it felt like from your spot on the floor or the field, in the specific moments only you saw.',
    write_about: 'The hardest moment of the season from your point of view. One thing you noticed that they did not see from the bench. What you would change next season if you were them, said respectfully and honestly.',
  },
];

function ReaderOptions() {
  return (
    <div className="mt-6 space-y-4">
      {READERS.map((r) => {
        const isEm = r.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={r.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">{r.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{r.name}</h4>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <ReaderBlock label="What they know" body={r.knows} />
              <ReaderBlock label="What they do not know" body={r.does_not} />
              <ReaderBlock label="Worth writing about" body={r.write_about} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReaderBlock({ label, body }: { label: string; body: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
        {label}
      </p>
      <p className="mt-1.5 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Section 2: The five moves --- */
function FiveMoves() {
  return (
    <Section id="sec-five-moves" kicker="The five moves" title="The map for the next four stages.">
      <p className="mt-6 text-text/90">
        Every move from this week applies. Today you run them on one real piece
        for one real reader. Stages 1 through 4 each take one move and do it on
        this specific brief. Move three (connect what does not obviously connect)
        is folded into the others.
      </p>
      <MovesDiagram />
      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The order matters
        </p>
        <p className="mt-3 text-text/90">
          Reader first, then through-line, then particulars, then defense pass.
          If you write before you have the reader, you write to an average human.
          If you find particulars before you have a through-line, you cannot tell
          which ones to keep. The order is the move.
        </p>
      </div>
    </Section>
  );
}

const MOVES = [
  {
    n: '01',
    name: 'Reader',
    lesson: 'Stage 1',
    accent: 'emerald' as const,
    output: 'One real person. What they know. What they do not. What they would want to hear from you.',
  },
  {
    n: '02',
    name: 'Through-line',
    lesson: 'Stage 2',
    accent: 'amber' as const,
    output: 'The one sentence the piece is about, said in language this specific reader would actually say.',
  },
  {
    n: '03',
    name: 'Particulars',
    lesson: 'Stage 3',
    accent: 'emerald' as const,
    output: 'A draft where every paragraph has at least one detail only you have access to.',
  },
  {
    n: '04',
    name: 'Defense',
    lesson: 'Stage 4',
    accent: 'amber' as const,
    output: 'A version you would still stand behind if the reader sent you a question about any sentence.',
  },
];

function MovesDiagram() {
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {MOVES.map((m) => {
        const isEm = m.accent === 'emerald';
        const tagCls = isEm ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber/15 text-amber';
        return (
          <div key={m.n} className="rounded-lg border border-line bg-surface p-4">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] text-muted">{m.n}</span>
              <span
                className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${tagCls}`}
              >
                {m.name}
              </span>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-muted">
              {m.lesson}
            </p>
            <p className="mt-2 text-sm text-text/85">
              <span className="block font-mono text-[10px] uppercase tracking-wider text-amber">
                Output
              </span>
              {m.output}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 3: Stage 1 Reader --- */
function Stage1() {
  return (
    <Section id="sec-stage-1" kicker="Stage 1 · Reader" title="Pick the person. Write down what they know.">
      <p className="mt-6 text-text/90">
        AI does not know your reader. You do. Spend five minutes on this on
        paper. Skip this and the rest of the chain works on an average human,
        which means it works on nobody.
      </p>

      <StageBlock
        label="The move you make on paper"
        accent="emerald"
        body="Write the reader’s name at the top. Under it, three short lists. What they already know about the topic. What they have never said to you but you think they would understand. One thing you have always wanted to tell them about this and have not. The third one is usually the through-line in disguise."
      />

      <StageBlock
        label="The prompt you give AI"
        accent="amber"
        body="“I am writing 500 words for [reader, described in two sentences]. My topic is [topic]. Help me list ten things this reader probably already knows about the topic, and ten things they do not. Do not write the piece. Do not draft anything. Just help me see the gap.” AI is good at making lists. You decide which list items are actually true about your reader."
      />

      <StageBlock
        label="The audit move you do right here"
        accent="emerald"
        body="Cross out anything on the AI list that is not actually true about your reader. AI does not know them. AI is guessing. If your sibling does not actually play soccer, &ldquo;sports analogies will land&rdquo; is wrong. The crossing-out is the move. The list that survives is the only list that matters."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <ul className="mt-3 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One reader, named.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            Three short lists: what they know, what they do not, what you have
            always wanted to tell them.
          </li>
          <li>
            <span className="font-mono text-[10px] text-amber mr-2">→</span>
            One candidate through-line, lifted from the third list.
          </li>
        </ul>
      </div>
    </Section>
  );
}

/* --- Section 4: Stage 2 Through-line --- */
function Stage2() {
  return (
    <Section id="sec-stage-2" kicker="Stage 2 · Through-line" title="One sentence. Said the way this reader would say it.">
      <p className="mt-6 text-text/90">
        From 4.2: the through-line is the one thing the piece is about, said in a
        single sentence the writer can say out loud. From today: the through-line
        is also said in the reader’s language. The candidate sentence from
        stage 1 is a draft. Now sharpen it.
      </p>

      <StageBlock
        label="The sharpening move"
        accent="amber"
        body="Take your candidate through-line. Read it out loud as if you were saying it to your reader, face to face. If it sounds like an essay sentence, rewrite it as a sentence you would actually say to them. If it has words you do not normally use, swap them for words you do. The reader has to hear you in the line, not an AI version of you."
      />

      <StageBlock
        label="The swap test, from 4.2"
        accent="emerald"
        body="Sketch four paragraphs as one-line bullets. Then try them in three different orders. If the order does not matter, the through-line is not strong enough yet. If one order is obviously better, that is your sequence. The reader will follow it for the same reason it felt right to you."
      />

      <StageBlock
        label="The prompt that helps"
        accent="amber"
        body="“Here is my through-line. Here is my reader described in one paragraph. Tell me three questions the reader would ask after the first sentence. Do not answer them. Do not rewrite anything.” The questions show you what the reader expects next. The piece is structured around answering those questions in your voice, with your particulars."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          One sentence that says what the piece is about, in words you would
          actually use with this reader. A rough four-paragraph sequence that
          flows from the sentence. Three reader questions noted in the margin to
          answer as you write.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 5: Stage 3 Particulars --- */
function Stage3() {
  return (
    <Section id="sec-stage-3" kicker="Stage 3 · Particulars" title="Find what only you have. Put one in every paragraph.">
      <p className="mt-6 text-text/90">
        From 4.3: AI generalizes, you particularize. The rule today is stricter
        than 4.3. Every paragraph in this 500 needs at least one particular only
        you have. No exceptions. Without a particular, the paragraph is decorative.
      </p>

      <StageBlock
        label="The mining move"
        accent="emerald"
        body="For each of your four paragraph bullets, open your notebook (or a fresh doc) and answer three questions: what specific moment from my life relates to this point, what is one sentence somebody actually said about this, what is one number from my own life that connects. Most of what you write down will not make the essay. One thing per paragraph will."
      />

      <StageBlock
        label="The drafting move"
        accent="amber"
        body="Open a clean doc. Write the through-line as the opening sentence. Write each paragraph using one particular per paragraph as the anchor. Around the particular, build the argument from your stage 2 questions. Use drafting modes from 3.3: expander for body paragraphs, polish only after the full rough draft exists. No AI for the particulars. Only for the structure around them."
      />

      <StageBlock
        label="The trap to watch for"
        accent="emerald"
        body="AI will offer to &ldquo;add a personal touch&rdquo; or &ldquo;include a relevant example.&rdquo; If you accept an AI-generated example, you have lost the move. Examples are particulars. Particulars are yours. AI examples are averaged. They feel real and read fake. Catch them and replace them with something from your actual week."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A complete rough draft. Roughly 500 words. Every paragraph has at least
          one particular only you could have written. The through-line is in the
          opening sentence and recurs in the close. Rough at the edges. That is
          fine.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 6: Stage 4 Defense --- */
function Stage4() {
  return (
    <Section id="sec-stage-4" kicker="Stage 4 · Defense + ship" title="Defense pass. Then read it to your reader in your head.">
      <p className="mt-6 text-text/90">
        From 4.4: a defensible draft is true, yours, and traceable. From today:
        you do not have a teacher to defend to. You have one reader. The defense
        pass is the same. The audience for the defense is the person you wrote
        this for.
      </p>

      <StageBlock
        label="Pass 1 · The defense kit"
        accent="emerald"
        body="Run the three-piece kit from 4.4 on this draft. Claim list: every name, number, quote, paired with where you got it. Paragraph reasons: every paragraph paired with one sentence on why it is in the piece. AI moves: every AI suggestion you accepted, paired with why. Three columns, ten minutes. If any row is empty, the cell either gets filled or the line gets cut."
      />

      <StageBlock
        label="Pass 2 · The reader pass"
        accent="amber"
        body="Read the whole draft out loud as if your reader were on a phone call with you. Stop on any sentence that does not sound like something you would say to them. Rewrite it. Pay attention to: words you would not use with this person, jokes that would not land with this person, paragraphs that sound like they are written for somebody else. This is the audit move from 3.4 with the reader in the room."
      />

      <StageBlock
        label="Ship"
        accent="emerald"
        body="Send the piece to your reader. Actually send it. A text, an email, a printed page, a handwritten letter. The ship in this lesson is not a teacher’s inbox. It is the person you wrote it for. If you cannot bring yourself to send it, find the sentence that scares you and rewrite that one. Then send."
      />

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Output of this stage
        </p>
        <p className="mt-3 text-text/90">
          A sent piece. A reader who actually receives it. One sentence in your
          notes about what was hardest in the chain. The chain ends with the
          reader on the other end, not with submit.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 7: Carry forward --- */
function CarryForward() {
  return (
    <Section id="sec-forward" kicker="What you carry forward" title="Four habits to keep. Four traps to remember.">
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Habits to keep
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">01</span>
              Pick a real reader before you write a single sentence.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">02</span>
              Say the through-line out loud before you keep it.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">03</span>
              One particular per paragraph, minimum. No exceptions.
            </li>
            <li>
              <span className="font-mono text-[10px] text-emerald-300 mr-2">04</span>
              Run the defense kit before submit. Every time.
            </li>
          </ul>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Traps to remember
          </p>
          <ul className="mt-4 space-y-3 text-sm text-text/90">
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">01</span>
              Writing for &ldquo;a general audience.&rdquo; That is no audience.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">02</span>
              A through-line you can only say in essay language. If the reader
              would not say it, neither should you.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">03</span>
              Accepting AI examples. Examples are particulars. Particulars are
              yours.
            </li>
            <li>
              <span className="font-mono text-[10px] text-rose-300 mr-2">04</span>
              Treating ship as submit. Ship is when the reader actually receives
              it.
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The bigger move
        </p>
        <p className="mt-3 text-text/90">
          AI can generate writing all day. AI cannot care about who reads it.
          Caring is the move that makes work feel made-for-someone instead of
          made-by-someone. Real readers can feel the difference. The skill is to
          do this on purpose, every time, whether the reader is a sibling, a
          coach, a future boss, or a customer.
        </p>
      </div>
    </Section>
  );
}

/* --- Section 8: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card · Week 4" title="Screenshot this. The whole week on one card.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Week 4 · Lock card · Doing the work AI cannot do
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          AI generates. You decide what matters, what is specific, what holds up,
          and who it is for. That is the whole job.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <MoveTile n="01" name="Matters" lesson="4.2" body="Through-line first. Cut 30%. Say the point out loud or you do not have one." />
          <MoveTile n="02" name="Specific" lesson="4.3" body="One particular per paragraph. A moment, a quote, a real number. AI averages, you particularize." />
          <MoveTile n="03" name="Defensible" lesson="4.4" body="Claim list, paragraph reasons, AI moves. Be ready for where, why, what would you change." />
          <MoveTile n="04" name="Reader" lesson="4.5" body="One real person. What they know. What they do not. Land it with them, not an average human." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The Week 4 vocab spine
          </p>
          <p className="mt-3 text-text/90">
            <span className="font-mono text-[10px] text-amber mr-2">4.1</span>
            Judgment ·
            <span className="font-mono text-[10px] text-amber mx-2">4.2</span>
            Through-line ·
            <span className="font-mono text-[10px] text-amber mx-2">4.3</span>
            Particular ·
            <span className="font-mono text-[10px] text-amber mx-2">4.4</span>
            Defensible
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Halfway home. Four weeks in: you can run the chain and you can do the
          work AI cannot do. Four weeks to go on how to take this into the wild.
        </p>
      </div>
    </Section>
  );
}

function MoveTile({
  n,
  name,
  lesson,
  body,
}: {
  n: string;
  name: string;
  lesson: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
        <span className="font-mono text-[10px] text-muted">{lesson}</span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Week 5
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Taking the chain into the wild
          </h3>
          <p className="mt-3 text-sm text-text/85">
            School is one place to run the chain. The rest of your life is the
            other place. Week 5 takes everything from weeks 1 through 4 and
            applies it to writing that is not for a grade: letters, applications,
            things you publish, things you send. Same moves. Higher stakes.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Stage helper --- */
function StageBlock({
  label,
  body,
  accent,
}: {
  label: string;
  body: string;
  accent: 'emerald' | 'amber';
}) {
  const isEm = accent === 'emerald';
  const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`mt-5 rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
        {label}
      </p>
      <p className="mt-3 text-text/90">{body}</p>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'The brief',
    'The five moves',
    '1 · Reader',
    '2 · Through-line',
    '3 · Particulars',
    '4 · Defense + ship',
    'Carry forward',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
